import {ThemeProvider, useTheme} from '@volkswagen-onehub/components-core'
import {AppConfig} from '../common/configuration'
import PromotionalPackageDefaultCopy from '../translations/promotionalPackageDefaultCopy'
import OverviewStructuredDataInterface from '../types/overviewStructuredDataInterface'
import SingleModelInterface, {Promotion} from '../types/singleModelInterface'
import {useFeatureServices} from '../use-feature-services'
import ScrollToTop from './components/views/ScrollToTop'
import {SinglePromotionList} from './components/views/singlePromotionList'

import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {
    setApiQueryParams,
    setBackendUrl,
    setBaseUrl,
    setBrand,
    setCampaignId,
    setCarsByCustomerTypeNumber,
    setFivePlusLayerPath,
    setIsShowroom,
    setLoading,
    setModelFromCms,
    setPreviewAPIToken,
    setSelectedPromotion,
    setShowRFQForm,
    setTrackingBrand,
} from '../features/general/generalSlice'
import Brands from '../utils/brand/brand'
import {
    AppDispatch,
    fetchAllStages,
    fetchAllTeasers,
    fetchMyDealerBranches,
    getDealerName,
} from './thunk'

// List components
import {Route, Router, Switch} from 'react-router-dom'
import {Loading} from '../common/components/general/loading'

import {useTranslation} from 'react-i18next'
import {RootState} from './rootReducer'

// tslint:disable-next-line:no-submodule-imports
import format from 'date-fns/format'
// tslint:disable-next-line:no-submodule-imports
import itLocale from 'date-fns/locale/it'
import filterByCustomerType from '../utils/filters/filterByCustomerType'
import getBackendUrlFromBrand from '../utils/url/getBackendUrlFromBrand'
import UrlRoutes from '../utils/url/urlRoutes'
import addTrailingSlash from '../utils/url/addTrailingSlash'
import {authServiceHelpers} from '../shared/authServiceHelper'
import {SingleStageInterface} from '../types/stage'
import {
    setDealerBranches,
    setDealerCode,
} from '../features/dealerData/dealerDataSlice'
import {BackendEndpoints} from '../utils/url/BackendEndpoints'
import {FetchDealer} from '../types/dealer'
import {PromotionContent} from './components/promotion/PromotionContent'
import {
    openCouponLayer,
    setCouponLayerHandle,
} from './components/layers/launchers/CouponRequestLayerHandle'
import {setAlertSystemLayerHandle} from './components/layers/launchers/AlertSystemLayerHandle'
import {setDisclaimerLayerHandle} from './components/layers/launchers/DisclaimerLayerHandle'
import {setTrackingServiceManager} from '../utils/tracking/tracking-utils/setLocalTracking'
import {Global, LayoutType, Params} from '../shared/variables'
import {
    setAutoloadedRFQForm,
    setFormCycleID,
    setFormPromotion,
} from '../features/userData/userDataSlice'
import {findPromotionByFormOpeningTag} from '../utils/promos/findPromotionByFormOpeningTag'
import {getRFQLandingParams} from '../utils/url/getRFQLandingParams'
import {generateRandomString} from '../utils/tracking/tracking-utils/generateRandomString'
import {isMobile} from '../shared/utilities'
import {setCurrentThemeVariant} from '../shared/themeVariant'

export function App(props: AppConfig): React.JSX.Element {
    // tslint:disable-next-line:no-any
    const {'s2:history': historyService} = useFeatureServices() as any
    const theme = useTheme()
    const {'vw:authService': provider} = useFeatureServices() as any
    const {'vw:authservice-config': authserviceConfig} =
        useFeatureServices() as any
    const {'service-config-provider': serviceConfigProvider} =
        useFeatureServices() as any
    const {'mydealer-service': myDealerService} = useFeatureServices() as any
    const {'layer-manager': layerManager} = useFeatureServices() as any
    const {'cms-service': cmsService} = useFeatureServices() as any
    const {tracking: trackingManager} = useFeatureServices() as any

    const authService = provider?.register('market')
    const logged = useSelector((state: RootState) => state.general.logged)

    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    // NOTE: usiamo direttamente la prop ricevuta dal Feature App (non lo store)
    // per evitare un primo render con un valore "stale" che farebbe partire una
    // fetch `cars/<slug>` con lo slug della pagina precedente (es. id4 → polo
    // farebbe partire sia `cars/id4` che `cars/polo`). Lo store viene comunque
    // sincronizzato nello `useEffect` più sotto per eventuali altri consumer.
    const modelFromCms = props.modelFromCms ?? ''
    const carCallOver = useSelector(
        (state: RootState) => state.general.carCallOver
    )
    const singleCar = useSelector((state: RootState) => state.general.singleCar)
    const modelsWithActivePromotions = useSelector(
        (state: RootState) => state.general.modelsWithActivePromotions
    )
    const numberOfModelsWithActivePromotions = useSelector(
        (state: RootState) => state.general.numberOfModelsWithActivePromotions
    )
    // tslint:disable-next-line:no-unnecessary-type-assertion
    const dispatch = useDispatch() as AppDispatch
    const {t} = useTranslation('app')
    const currentMonth = format(new Date(), 'MMMM', {locale: itLocale})
    const customerType = useSelector(
        (state: RootState) => state.general.customerType
    )
    const brandFromState = useSelector(
        (state: RootState) => state.general.brand
    )

    const stageImages = useSelector(
        (state: RootState) => state.stages.allstages
    )

    const allTeasers = useSelector(
        (state: RootState) => state.teasers.allTeasers
    )
    const dealerCode = useSelector(
        (state: RootState) => state.dealerData.dealerCode
    )

    const [stageImage, setStageImage] = useState({} as SingleStageInterface)
    const [hasFetchedTeasers, setHasFetchedTeasers] = useState(false)
    const [hasFetchedStages, setHasFetchedStages] = useState(false)
    const [isReadyToRender, setIsReadyToRender] = useState(false)
    const [search, setSearch] = useState(window.location.search)

    const modelWithPVV = modelsWithActivePromotions.filter(
        (model: SingleModelInterface) =>
            model.related_promotions.some(
                (promo: Promotion) =>
                    promo.title === PromotionalPackageDefaultCopy.TITLE
            )
    )

    const dynamicPromoPVV: OverviewStructuredDataInterface[] = []

    modelWithPVV.map((model: SingleModelInterface, index: number) => {
        const pvvPromo = model.related_promotions.filter(
            (promo: Promotion) =>
                promo.title === PromotionalPackageDefaultCopy.TITLE
        )
        const pvvTitle = `${pvvPromo[0].title} ${pvvPromo[0].price_prefix} ${pvvPromo[0].price}€ ${pvvPromo[0].price_suffix}`
        const formattedPvvTitle = pvvTitle.replace(/<br\s*\/?>/gi, ' ')

        dynamicPromoPVV.push({
            '@type': 'ListItem',
            'position': index,
            'item': {
                '@type': 'Offer',
                'priceSpecification': {
                    '@type': 'UnitPriceSpecification',
                    'priceCurrency': 'EUR',
                    'price': formattedPvvTitle,
                },
                'itemOffered': {
                    '@type': 'Car',
                    'name': pvvPromo[0].sub_title,
                    'itemCondition': 'https://schema.org/NewCondition',
                },
            },
        })
    })

    const overviewStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        'itemListElement': dynamicPromoPVV,
    }

    const backendUrlWithBrand = useMemo(() => {
        if (props.brand && props.backendUrl) {
            return getBackendUrlFromBrand(props.backendUrl, props.brand)
        }

        return ''
    }, [props.backendUrl, props.brand])

    const createEndpoint = (path: string) => ({
        ENDPOINT: addTrailingSlash(backendUrlWithBrand) + path,
    })

    const teaserEndpoint = useMemo(
        () => (backendUrlWithBrand ? createEndpoint('teasers') : null),
        [backendUrlWithBrand]
    )

    const stageEndpoint = useMemo(
        () => (backendUrlWithBrand ? createEndpoint('stage') : null),
        [backendUrlWithBrand]
    )

    useEffect(() => {
        const currentUrl = new URL(window.location.href)
        const query = currentUrl.searchParams
        const campaign = query.get(Params.CAMPAIGN)

        if (!campaign) {
            return
        }

        let newCampaignID = ''

        if (campaign.includes(Params.AUTONOMESPOTIFY)) {
            newCampaignID = Params.PDSSFDW
        } else if (campaign.includes(Params.AUTONOMEDEM)) {
            newCampaignID = Params.PCRXXDW
        } else if (campaign.includes(Params.AUTONOMEPMAX)) {
            newCampaignID = Params.PDSPXDW
        }

        if (!newCampaignID) {
            return
        }
        if (query.get(Params.CAMPAIGN_ID) === newCampaignID) {
            return
        }

        query.set(Params.CAMPAIGN_ID, newCampaignID)
        currentUrl.search = query.toString()
        window.location.href = currentUrl.toString()
    }, [])

    useEffect(() => {
        setCouponLayerHandle(layerManager)
        setAlertSystemLayerHandle(layerManager)
        setDisclaimerLayerHandle(layerManager)

        setTrackingServiceManager(trackingManager)
    }, [])

    useEffect(() => {
        if (logged === undefined) {
            dispatch(setLoading(true))
        } else {
            dispatch(setLoading(false))
        }

        if (authService) {
            authServiceHelpers(
                authService,
                authserviceConfig,
                serviceConfigProvider
            )
        }
    }, [logged])

    useEffect(() => {
        if (brandFromState === Brands.VIC) {
            const carsByCustomerType = filterByCustomerType(
                customerType,
                modelsWithActivePromotions
            )
            dispatch(setCarsByCustomerTypeNumber(carsByCustomerType.length))

            return
        }

        dispatch(setCarsByCustomerTypeNumber(modelsWithActivePromotions.length))
    }, [customerType, dispatch, modelsWithActivePromotions, brandFromState])

    useEffect(() => {
        const layout = typeof props.layout !== 'undefined' && props.layout
        dispatch(setIsShowroom(layout === LayoutType.SHOWROOM))
        dispatch(setModelFromCms(props.modelFromCms))
    }, [props.layout, props.modelFromCms])

    useEffect(() => {
        if (typeof props.baseUrl !== 'undefined') {
            dispatch(setBaseUrl(props.baseUrl))
        }
    }, [props.baseUrl])

    useEffect(() => {
        if (carCallOver) {
            dispatch(setLoading(false))
        }
    }, [carCallOver])

    useEffect(() => {
        const previewDate =
            typeof props.preview_date === 'undefined' ? '' : props.preview_date
        const previewAPIToken =
            typeof props.previewAPIToken === 'undefined'
                ? ''
                : props.previewAPIToken
        const brand = props.brand ?? ''
        const trackingBrand = props.trackingBrand ?? ''

        dispatch(setPreviewAPIToken(previewAPIToken))
        dispatch(setApiQueryParams(previewDate))
        dispatch(setBackendUrl(backendUrlWithBrand))
        dispatch(setBrand(brand))
        dispatch(setTrackingBrand(trackingBrand))
    }, [
        props.backendUrl,
        props.preview_date,
        props.trackingBrand,
        props.previewAPIToken,
        props.brand,
        numberOfModelsWithActivePromotions,
    ])

    useEffect(() => {
        // `mydealer-service` is an optional Feature Service: when the
        // integrator does not provide it there is no dealer to wait for, so the
        // app has to be unblocked right away.
        if (!myDealerService) {
            dispatch(setLoading(false))
            setIsReadyToRender(true)

            return
        }

        if (myDealerService) {
            let myDealerCode = myDealerService.getSelectedDealer()
            setIsReadyToRender(false)
            dispatch(setLoading(true))

            if (myDealerCode) {
                dispatch(setDealerCode(myDealerCode.id))
            }

            myDealerService.subscribe((dealerId: any) => {
                if (dealerId) {
                    myDealerCode = dealerId.id
                    if (myDealerCode !== '') {
                        dispatch(setDealerCode(myDealerCode))
                        if (props.brand && props.backendUrl) {
                            const dealerEndpointUrl: FetchDealer = {
                                ENDPOINT:
                                    addTrailingSlash(
                                        addTrailingSlash(backendUrlWithBrand) +
                                            BackendEndpoints.DEALER
                                    ) + myDealerCode,
                            }
                            dispatch(getDealerName(dealerEndpointUrl))
                        }
                    }
                } else {
                    dispatch(setDealerCode(''))
                }
            })

            setTimeout(() => {
                dispatch(setLoading(false))
                setIsReadyToRender(true)
            }, 300)
        }
    }, [myDealerService])

    useEffect(() => {
        if (props.showRFQForm === undefined) {
            return
        }

        const shouldShowRFQ = props.showRFQForm === 'yes'

        dispatch(setShowRFQForm(shouldShowRFQ))
    }, [props.showRFQForm])

    useEffect(() => {
        dispatch(setFivePlusLayerPath(props.fivePlusLayerPath))
    }, [props.fivePlusLayerPath])

    useEffect(() => {
        if (dealerCode) {
            dispatch(fetchMyDealerBranches(dealerCode))
        } else {
            dispatch(setDealerBranches([]))
        }
    }, [dealerCode])

    useEffect(() => {
        if (teaserEndpoint && !hasFetchedTeasers) {
            dispatch(fetchAllTeasers(teaserEndpoint))
                .unwrap()
                .then()
                .catch((err: any) => console.log(err))
                .then(() => {
                    setHasFetchedTeasers(true)
                })
        }
    }, [teaserEndpoint, hasFetchedTeasers])

    useEffect(() => {
        if (stageEndpoint && !hasFetchedStages) {
            dispatch(fetchAllStages(stageEndpoint))
                .unwrap()
                .then(() => {
                    setHasFetchedStages(true)
                })
                .catch((err: any) => console.log(err))
        }
    }, [stageEndpoint, hasFetchedStages])

    const singleCarRoute = (): string => {
        if (brandFromState === Brands.VIC) {
            return `/${UrlRoutes.CUSTOMER_TYPE}/:customerType/${UrlRoutes.SINGLE_CAR}/:modelName`
        }

        return `/${UrlRoutes.SINGLE_CAR}/:modelName`
    }

    useEffect(() => {
        if (stageImages) {
            setStageImage(
                stageImages.find(
                    (stage: SingleStageInterface) => stage.type === customerType
                )
            )
        }
    }, [customerType, stageImages])

    useEffect(() => {
        const observer = setInterval(() => {
            if (window.location.search !== search) {
                setSearch(window.location.search)
            }
        }, 200)

        return () => clearInterval(observer)
    }, [search])

    useEffect(() => {
        if (props.showRFQForm !== 'yes' || cmsService?.inEditor) {
            return
        }

        if (
            !modelsWithActivePromotions ||
            modelsWithActivePromotions.length === 0
        ) {
            return
        }

        if (
            getRFQLandingParams(search).shouldOpenForm ||
            props.layout === LayoutType.SHOWROOM
        ) {
            return
        }

        const rawTiming = isMobile
            ? props.autoloadRFQFormTimingMobile
            : props.autoloadRFQFormTimingDesktop

        if (!rawTiming) {
            return
        }

        const delay = parseInt(rawTiming, 10)

        if (!Number.isFinite(delay) || delay <= 0) {
            return
        }

        const timer = setTimeout(() => {
            dispatch(setAutoloadedRFQForm(true))

            dispatch(
                setFormCycleID(generateRandomString(Global.CYCLE_ID_LENGTH))
            )
            openCouponLayer()
        }, delay)

        return () => clearTimeout(timer)
    }, [
        props.showRFQForm,
        props.autoloadRFQFormTimingDesktop,
        props.autoloadRFQFormTimingMobile,
        search,
        cmsService?.inEditor,
        modelsWithActivePromotions,
    ])

    // La form può aprirsi quando ha i dati che le servono: la lista modelli
    // (select del modello, pagina listing) oppure il modello corrente (pagina
    // di dettaglio / showroom, dove la lista non viene caricata).
    const isRFQFormDataReady =
        modelsWithActivePromotions?.length > 0 || Boolean(singleCar?.id)

    useEffect(() => {
        const {shouldOpenForm, promotionTag} = getRFQLandingParams(search)

        if (
            shouldOpenForm &&
            props.showRFQForm === 'yes' &&
            !cmsService?.inEditor &&
            isRFQFormDataReady
        ) {
            // La promozione si cerca solo nel modello di contesto (dettaglio /
            // showroom): in listing la form non è legata a un modello, e
            // mostrare le condizioni di una promo lasciando libera la scelta
            // del modello sarebbe incoerente. Se nessuna promo ha il tag (es.
            // URL della campagna creata prima della promo) la form si apre
            // comunque, senza riepilogo.
            const promotion = findPromotionByFormOpeningTag(
                singleCar?.related_promotions,
                promotionTag
            )

            if (promotion) {
                dispatch(
                    setSelectedPromotion({
                        id: promotion.id,
                        title: promotion.title,
                        motorization: promotion.motorization,
                    })
                )
                dispatch(setFormPromotion(promotion))
            }

            dispatch(
                setFormCycleID(generateRandomString(Global.CYCLE_ID_LENGTH))
            )
            openCouponLayer()
        } else if (props.showRFQForm === 'yes') {
            dispatch(setSelectedPromotion(null))
        }
    }, [search, props.showRFQForm, cmsService?.inEditor, isRFQFormDataReady])

    useEffect(() => {
        const CAMPAING_ID_KEY = 'vwa_d6_cms.campaignID'

        if (sessionStorage.getItem(CAMPAING_ID_KEY)) {
            dispatch(
                setCampaignId(sessionStorage.getItem(CAMPAING_ID_KEY) || '')
            )
        }
    }, [])

    useEffect(() => {
        setCurrentThemeVariant(theme.variant)
    }, [theme.variant])

    return (
        <ThemeProvider>
            {isReadyToRender || isShowroom ? (
                <>
                    <Router history={historyService.history}>
                        <Switch>
                            <Route exact path={singleCarRoute()}>
                                <ScrollToTop />
                                <SinglePromotionList />
                            </Route>
                            <Route
                                exact
                                path={[
                                    '/',
                                    `/${UrlRoutes.CUSTOMER_TYPE}/:customerType`,
                                ]}
                            >
                                <PromotionContent
                                    isShowroom={isShowroom}
                                    modelFromCms={modelFromCms}
                                    singleCar={singleCar}
                                    brandFromState={brandFromState}
                                    stageImage={stageImage}
                                    t={t}
                                    currentMonth={currentMonth}
                                    overviewStructuredData={
                                        overviewStructuredData
                                    }
                                    allTeasers={allTeasers}
                                />
                            </Route>
                        </Switch>
                    </Router>
                </>
            ) : (
                <Loading />
            )}
        </ThemeProvider>
    )
}
